import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {FaRotateLeft} from "react-icons/fa6";
import {MaterialMovementsContext} from "../material-movements.context.jsx";
import projectUtils from "../../../../../utils/project-utils.js";
import {Modal} from "../../../../../components/modal/modal.jsx";
import materialMovementService from "../../../../../services/web/material-movement-service.js";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import {FaPen, FaPlus} from "react-icons/fa";
import Button from "../../../../../components/button/button.jsx";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import useHasPermission from "../../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";
import InputForm from "../../../../../components/input/input-form.jsx";
import {toast} from "react-toastify";
import DevolutionCellRenderer from "./devolution-cell-renderer.jsx";
import ObservationCellRenderer from "./observation-cell-renderer.jsx";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import InputSwitch from "../../../../../components/input/input-switch.jsx";
import {clearRequiredFieldError, markRequiredFields} from "../../../../../utils/project-utils.js";

let openModalBreakdown = false, openModalObservation = false
const Devolution = ({storageSelected}) => {
    const {userSelected, setIsLoading, refetchCautions} = useContext(MaterialMovementsContext)
    const [openModal, setOpenModal] = useState(false)
    const [devolutionByThirdParty, setDevolutionByThirdParty] = useState(false)
    const [thirdPartyObservation, setThirdPartyObservation] = useState("")
    const [password, setPassword] = useState("")
    const [cautions, setCautions] = useState([])
    const [showPermanent, setShowPermanent] = useState(false)
    const devolutionSelectionRef = useRef(null)
    const saveButtonRef = useRef(null)
    const passwordInputRef = useRef(null)
    const hasSelectedDevolution = cautions.some((caution) => Number(caution.quantityReturn) > 0)

    const {data: dataCautions, fetchStatus, isFetching: isLoadingCaution} = useQuery({
        queryKey: ['cautions-devolution', userSelected],
        queryFn: () => materialMovementService.getByUserAndStorage(userSelected.value, storageSelected.value),
        enabled: !!openModal
    });

    useEffect(() => {
        if (fetchStatus === "idle" && dataCautions) {
            setCautions(dataCautions)
        }
    }, [dataCautions, fetchStatus]);

    useEffect(() => {
        if (!devolutionByThirdParty) {
            setThirdPartyObservation("")
        }
    }, [devolutionByThirdParty]);

    useEffect(() => {
        setIsLoading(isLoadingCaution)
    }, [isLoadingCaution]);

    useEffect(() => {
        if (!openModal) {
            setDevolutionByThirdParty(false)
            setThirdPartyObservation("")
            setPassword("")
            setCautions([])
            setShowPermanent(false)
        }
    }, [openModal]);

    useEffect(() => {
        clearRequiredFieldError(devolutionSelectionRef.current)
    }, [hasSelectedDevolution]);

    const columnDefs = useMemo(() => [
        {headerName: "Entrega", field: "date", width: 140},
        {headerName: "Depósito", field: "storage_description", sortable: false},
        {headerName: "Material", field: "material_description", flex: 1, sortable: false},
        {headerName: "Quantidade", field: "quantity", sortable: false, width: 100, hozAlign: "center"},
        {headerName: "Observação inicial", field: "observations", flex: 1, sortable: false, width: 280, hozAlign: "center"},

        {headerName: "Devolver *", field: "", width: 90,
            cellRenderer: DevolutionCellRenderer,
            cellRendererParams: {
                setCautions
            }
        },
        {headerName: "Avaria", field: "", width: 60,
            cellRenderer: BreakdownCellRenderer,
            cellRendererParams: {
                setCautions
            }
        },
        {headerName: "Observação final", field: "", width: 130,
            cellRenderer: ObservationCellRenderer,
            cellRendererParams: {
                setCautions,
                setOpenModalObservation: (value) => openModalObservation = value,
                label: "Observação final",
            }
        },
    ], []);

    const defaultColDef = useMemo(() => {
        return {
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const showModal = () => {
        if (!userSelected) {
            projectUtils.swalMessage('warning', '', `Selecione um solicitante.`, 'Ok');
            return;
        }

        setOpenModal(true)
    }

    const save = () => {
        const invalidRequiredFields = markRequiredFields();
        if (invalidRequiredFields?.length) {
            projectUtils.swalErrorMessage('', 'Preencha os campos obrigatórios.', 'Ok');
            return;
        }

        setIsLoading(true);
        const currentPassword = passwordInputRef.current?.value ?? password;

        const formData = new FormData()

        const materialsDevolution = cautions.filter(c => c.quantityReturn && c.quantityReturn > 0).map(c => ({
            caution_id: c.id,
            movement_id: c.movement_id,
            quantity: c.quantityReturn,
            breakdown: c.breakdown,
            final_observations: c.observation || ""
        }))

        formData.append('requester', userSelected.value);
        formData.append('materials-devolution', JSON.stringify(materialsDevolution));
        formData.append('storage-selected', storageSelected.value);
        formData.append('thirdParty', devolutionByThirdParty);
        formData.append('thirdPartyObservation', thirdPartyObservation);
        formData.append('password', currentPassword);
        
        materialMovementService.saveDevolution(formData)
            .then(() => {
                toast.success("Devolução efetuada com sucesso")
                refetchCautions()
                setTimeout(() => setOpenModal(false), 1000)
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false))
    }

    useGlobalKey("Enter", () => {
        if (openModal && !openModalBreakdown && !openModalObservation) saveButtonRef.current?.click()
    })

    const hasPermission = useHasPermission()
    const removedByThirdPerson = hasPermission(PermissionEnum.MATERIALS_REMOVED_BY_THIRD_PERSON)

    return (
        <>
            <button onClick={showModal} className={"w-[30%] m-2 flex items-center justify-center rounded-[5px] h-[70px] bg-[#FC6D00] border-[#FC6D00] text-white cursor-pointer hover:bg-[#f7a54a]"}>
                <FaRotateLeft size={25}/>
                <span className={"ml-2 text-[18px] uppercase font-bold"}>Devolução</span>
            </button>

            <Modal alignTop={true} open={openModal} setOpen={setOpenModal} overflow={true} size="md" backdrop="static" keyboard={false} title={"Devolução"} className={"w-10/12"}>
                <Modal.Body className="p-2">
                    <div className={"my-2"}>
                        <InputSwitch
                            id={"show-permanent"}
                            label="Exibir materiais em cautela permanente"
                            checked={showPermanent}
                            onChange={(checked) => setShowPermanent(checked)}
                        />
                    </div>
                    <div ref={devolutionSelectionRef} tabIndex={-1} data-required-field="true" data-required-has-value={hasSelectedDevolution ? "true" : "false"}
                        className="min-h-[74px] border border-transparent rounded-md transition-[border-color,border-width] duration-150 ease-out">

                        <AgGridReact
                            rowData={showPermanent ? cautions : cautions.filter(c => !c.permanent)}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            getRowId={params => params.data?.id}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={aggridUtils.generalTheme()}
                            domLayout='autoHeight'
                            onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                        />

                    </div>
                </Modal.Body>
                <Modal.Footer className={"p-1"}>
                    <div className={"w-full flex justify-between"}>
                        <div className={"flex gap-4 w-6/12"}>
                            {
                                removedByThirdPerson &&
                                <>
                                    <InputCheckbox
                                        className={"w-[200px]"}
                                        id="third-party-checkbox"
                                        checked={devolutionByThirdParty}
                                        label="Devolução por terceiro"
                                        onChange={({target: {checked}}) => setDevolutionByThirdParty(checked)}/>

                                    <InputForm
                                        classNameWrapper={"w-[500px]"}
                                        disabled={!devolutionByThirdParty}
                                        inputClass={"bg-white"}
                                        id={`third-party-observation`}
                                        value={thirdPartyObservation}
                                        label={devolutionByThirdParty ? "Observação *" : "Observação"}
                                        name={"third-party-observation"}
                                        onChange={(e) => setThirdPartyObservation(e.target.value)}/>
                                </>
                            }
                        </div>

                        <div className={"flex gap-4"}>
                            <div className={"flex"}>
                                <div className={"min-w-[100px] bg-[#e0e0e0] border border-[#b2b2b2] h-9 p-2 -mr-px"}>
                                    <p><b>Solicitante:</b> {userSelected?.label}</p>
                                </div>
                                <InputForm
                                    disabled={devolutionByThirdParty}
                                    required
                                    inputClass={"bg-white !h-9 !border-[#b2b2b2] !rounded-[0px] !w-[100px]"}
                                    label={"Senha/PIN *"}
                                    id={`password`}
                                    type={"password"}
                                    value={password}
                                    name={"password"}
                                    inputRef={passwordInputRef}
                                    onChange={({target: {value}}) => setPassword(value)}/>
                            </div>
                            <Button
                                reference={saveButtonRef}
                                target={"warning"}
                                onClick={save}
                                actionFeedbackType="save"
                                loadingText="Devolvendo..."
                                successText="Devolução efetuada com sucesso"
                                className={"!h-9 bg-[#FC6D00] border-[#FC6D00]"}
                            >
                                Devolver
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Devolution;

const BreakdownCellRenderer = ({data, setCautions}) => {
    const {breakdown, id, is_unique} = data;
    const [openModal, setOpenModal] = useState(false)
    const [dataBreakdown, setData] = useState(breakdown)

    useEffect(() => {
        openModalBreakdown = openModal
    }, [openModal]);

    if(!is_unique) return

    const add = () => {
        setCautions(prev => prev.map(m => m.id === id ? ({...m, breakdown: dataBreakdown}) : m))
        setOpenModal(false)
    }

    return (
        <>
            <div className={"flex items-center justify-center h-full"}>
                <Button className={"!h-[25px] !w-[30px] !p-0"} onClick={() => setOpenModal(true)} title={"Adicionar avaria"}>
                    {breakdown ? <FaPen /> : <FaPlus /> }
                </Button>
            </div>

            <Modal alignTop={true} open={openModal} setOpen={setOpenModal} overflow={true} size="md" backdrop="static" keyboard={false} title={"Avaria"} className={"w-4/12"}>
                <Modal.Body className={"p-2 grid grid-cols-12 gap-3"}>
                    <InputTextarea
                        id={"breakdown"}
                        className={"col-span-12"}
                        textAreaClass={"h-40"}
                        label={"Descrição"}
                        value={dataBreakdown}
                        onChange={(e) => setData(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer className={"p-1"}>
                    <div className="w-full flex justify-end gap-3 m-0">
                        <Button target="primary" onClick={add}>Confirmar</Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    )
}